import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';
import { canInstallPWA, showInstallPrompt, isPWAInstalled } from '../lib/serviceWorker';

const InstallPWAPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isPWAInstalled()) {
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = new Date(dismissed).getTime();
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      if (dismissedTime > oneDayAgo) {
        return;
      }
    }

    // Listen for install prompt availability
    const checkInstall = () => {
      if (canInstallPWA()) {
        setCanInstall(true);
        setShowPrompt(true);
      }
    };

    // Check immediately and also on the beforeinstallprompt event
    const timer = setTimeout(checkInstall, 3000);
    window.addEventListener('beforeinstallprompt', () => {
      setCanInstall(true);
      setTimeout(() => setShowPrompt(true), 3000);
    });

    return () => clearTimeout(timer);
  }, []);

  const handleInstall = async () => {
    const accepted = await showInstallPrompt();
    if (accepted) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  if (!showPrompt || !canInstall) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 p-4 z-50 animate-in slide-in-from-bottom-5">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
      >
        <X className="h-5 w-5" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl">
          <Download className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 pr-6">
          <h3 className="font-semibold text-slate-800 text-sm">Install TribeHR</h3>
          <p className="text-xs text-slate-500 mt-1">
            Add to your home screen for quick access, offline support, and a native app experience.
          </p>
        </div>
      </div>
      
      <div className="flex gap-2 mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={handleDismiss}
          className="flex-1 text-xs"
        >
          Not now
        </Button>
        <Button
          size="sm"
          onClick={handleInstall}
          className="flex-1 text-xs bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
        >
          Install App
        </Button>
      </div>
    </div>
  );
};

export default InstallPWAPrompt;
